import React from 'react'
import { Logger } from 'wdc-cube'

import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'

import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import AssignmentIcon from '@mui/icons-material/Assignment'
import BadgeIcon from '@mui/icons-material/Badge'
import BusinessIcon from '@mui/icons-material/Business'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import GroupsIcon from '@mui/icons-material/Groups'
import HistoryIcon from '@mui/icons-material/History'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import RuleFolderIcon from '@mui/icons-material/RuleFolder'
import VisibilityIcon from '@mui/icons-material/Visibility'
import HomeIcon from '@mui/icons-material/Home'
import AssessmentIcon from '@mui/icons-material/Assessment'
import HubIcon from '@mui/icons-material/Hub'
import LayersIcon from '@mui/icons-material/Layers'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import PetsIcon from '@mui/icons-material/Pets'
import QueryStatsIcon from '@mui/icons-material/QueryStats'
import RequestQuoteIcon from '@mui/icons-material/RequestQuote'
import SettingsIcon from '@mui/icons-material/Settings'

import DatabaseIcon from 'src/components/icons/DatabaseIcon'
import StethoscopeIcon from 'src/components/icons/StethoscopeIcon'
import {
    FCClassContext,
    classToFComponent,
    sidebarDividerSx,
    sidebarNavIconSx,
    sidebarNavItemSx,
    sidebarNavSx
} from 'src/utils/views'
import { makeStyles } from 'tss-react/mui'
import { MainScope } from '../../Main.scopes'

const LOG = Logger.get('DrawerContent')

const useStyles = makeStyles()(() => ({
    listRoot: {
        flex: '1 1 auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        overflowX: 'hidden',
        '& .MuiListItemButton-root': {
            flexGrow: 0,
            flexShrink: 0
        },
        '& .MuiListItemText-root': {
            minWidth: 0
        },
        '& .MuiListItemText-primary': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        ...sidebarNavSx
    },
    settingsItem: {
        margin: '1px 10px',
        marginTop: 'auto',
        borderRadius: '10px',
        width: 'calc(100% - 20px)',
        ...sidebarNavItemSx
    },
    divider: {
        flexShrink: 0,
        ...sidebarDividerSx
    },
    svg: sidebarNavIconSx,
    itemButton: {
        margin: '1px 10px',
        borderRadius: '10px',
        ...sidebarNavItemSx
    }
}))

export type DrawerContentProps = {
    className?: string
    mainScope: MainScope
}

class DrawerContentClass implements FCClassContext<DrawerContentProps> {
    // :: Fields

    mainScope!: MainScope

    // :: emissors

    readonly emitOverview = () => this.mainScope.onOverview().catch(LOG.caught)
    readonly emitTheConnection = () => this.mainScope.onTheConnection().catch(LOG.caught)
    readonly emitTheMap = () => this.mainScope.onTheMap().catch(LOG.caught)
    readonly emitAnaliseDesempenho = () => this.mainScope.onAnaliseDesempenho().catch(LOG.caught)
    readonly emitTheActing = () => this.mainScope.onTheActing().catch(LOG.caught)
    readonly emitTheConsumerMarket = () => this.mainScope.onTheConsumerMarket().catch(LOG.caught)
    readonly emitMercadoVet = () => this.mainScope.onMercadoVet().catch(LOG.caught)
    readonly emitRDStation = () => this.mainScope.onRdStation().catch(LOG.caught)
    readonly emitUserManager = () => this.mainScope.onUserManager().catch(LOG.caught)
    readonly emitGroupManager = () => this.mainScope.onGroupManager().catch(LOG.caught)
    readonly emitConfigManager = () => this.mainScope.onConfigManager().catch(LOG.caught)
    readonly emitAccessAudit = () => this.mainScope.onAccessAudit().catch(LOG.caught)
    readonly emitBilling = () => this.mainScope.onBilling().catch(LOG.caught)
    readonly emitMcPj = () => this.mainScope.onMcPj().catch(LOG.caught)
    readonly emitMcPf = () => this.mainScope.onMcPf().catch(LOG.caught)
    readonly emitMcSerasa = () => this.mainScope.onMcSerasa().catch(LOG.caught)
    readonly emitMcSerasaHistory = () => this.mainScope.onMcSerasaHistory().catch(LOG.caught)
    readonly emitMcSerpro = () => this.mainScope.onMcSerpro().catch(LOG.caught)
    readonly emitMcGrupo = () => this.mainScope.onMcGrupo().catch(LOG.caught)
    readonly emitMcAnalise = () => this.mainScope.onMcAnalise().catch(LOG.caught)
    readonly emitMcBordero = () => this.mainScope.onMcBordero().catch(LOG.caught)
    readonly emitMcMonitoramento = () => this.mainScope.onMcMonitoramento().catch(LOG.caught)

    // :: Methods

    onSyncState({ mainScope }: DrawerContentProps) {
        this.mainScope = mainScope
    }

    render() {
        const scope = this.mainScope
        const { classes } = useStyles()

        return (
            <List component="nav" className={classes.listRoot}>
                <ListItemButton
                    className={classes.itemButton}
                    onClick={this.emitOverview}
                    data-active={scope.activePage === '/overview'}
                >
                    <Tooltip title="Início">
                        <ListItemIcon>
                            <HomeIcon className={classes.svg} />
                        </ListItemIcon>
                    </Tooltip>
                    <ListItemText primary="Início" />
                </ListItemButton>
                {scope.theConsumerMarketEnabled && (
                    <ListItemButton
                        className={classes.itemButton}
                        onClick={this.emitTheConsumerMarket}
                        data-active={scope.activePage === '/theConsumerMarket'}
                    >
                        <Tooltip title="Mercado IA">
                            <ListItemIcon>
                                <DatabaseIcon className={classes.svg} />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText primary="Mercado IA" />
                    </ListItemButton>
                )}
                {scope.mercadoVetEnabled && (
                    <ListItemButton
                        className={classes.itemButton}
                        onClick={this.emitMercadoVet}
                        data-active={scope.activePage === '/mercadoVet'}
                    >
                        <Tooltip title="Mercado Veterinário">
                            <ListItemIcon>
                                <PetsIcon className={classes.svg} />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText primary="Mercado Veterinário" />
                    </ListItemButton>
                )}
                {scope.theConnectionEnabled && (
                    <ListItemButton
                        className={classes.itemButton}
                        onClick={this.emitTheConnection}
                        data-active={scope.activePage === '/theConnection'}
                    >
                        <Tooltip title="Relações Societárias">
                            <ListItemIcon>
                                <HubIcon className={classes.svg} />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText primary="Relações Societárias" />
                    </ListItemButton>
                )}
                {scope.theActingEnabled && (
                    <ListItemButton
                        className={classes.itemButton}
                        onClick={this.emitTheActing}
                        data-active={scope.activePage === '/theActing'}
                    >
                        <Tooltip title="Profissionais Médicos">
                            <ListItemIcon>
                                <StethoscopeIcon className={classes.svg} />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText primary="Profissionais Médicos" />
                    </ListItemButton>
                )}
                {scope.theMapEnabled && (
                    <ListItemButton
                        className={classes.itemButton}
                        onClick={this.emitTheMap}
                        data-active={scope.activePage === '/theMap'}
                    >
                        <Tooltip title="The Map">
                            <ListItemIcon>
                                <MyLocationIcon className={classes.svg} />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText primary="The Map" />
                    </ListItemButton>
                )}
                {scope.analiseDesempenhoEnabled && (
                    <ListItemButton
                        className={classes.itemButton}
                        onClick={this.emitAnaliseDesempenho}
                        data-active={scope.activePage === '/analiseDesempenho'}
                    >
                        <Tooltip title="Análise de Desempenho">
                            <ListItemIcon>
                                <AssessmentIcon className={classes.svg} />
                            </ListItemIcon>
                        </Tooltip>
                        <ListItemText primary="Análise de Desempenho" />
                    </ListItemButton>
                )}

                {scope.rdStationEnabled && (
                    <>
                        <Divider sx={{ my: 1 }} className={classes.divider} />
                        <ListItemButton
                            className={classes.itemButton}
                            onClick={this.emitRDStation}
                            data-active={scope.activePage === '/rdStation'}
                        >
                            <Tooltip title="RD Station">
                                <ListItemIcon>
                                    <LayersIcon className={classes.svg} />
                                </ListItemIcon>
                            </Tooltip>
                            <ListItemText primary="RD Station" />
                        </ListItemButton>
                    </>
                )}
                {scope.motorCreditoEnabled && (
                    <>
                        <Divider sx={{ my: 1 }} className={classes.divider} />
                        {scope.mcBorderoEnabled && (
                            <ListItemButton
                                className={classes.itemButton}
                                onClick={this.emitMcBordero}
                                data-active={scope.activePage === '/mcBordero'}
                            >
                                <Tooltip title="Borderôs">
                                    <ListItemIcon>
                                        <ReceiptLongIcon className={classes.svg} />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText primary="Borderôs" />
                            </ListItemButton>
                        )}
                        {scope.mcAnaliseEnabled && (
                            <ListItemButton
                                className={classes.itemButton}
                                onClick={this.emitMcAnalise}
                                data-active={scope.activePage === '/mcAnalise'}
                            >
                                <Tooltip title="Análises">
                                    <ListItemIcon>
                                        <RuleFolderIcon className={classes.svg} />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText primary="Análises" />
                            </ListItemButton>
                        )}
                        {scope.mcGrupoEnabled && (
                            <ListItemButton
                                className={classes.itemButton}
                                onClick={this.emitMcGrupo}
                                data-active={scope.activePage === '/mcGrupo'}
                            >
                                <Tooltip title="Grupos de Análise">
                                    <ListItemIcon>
                                        <AssignmentIcon className={classes.svg} />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText primary="Grupos de Análise" />
                            </ListItemButton>
                        )}
                        {scope.mcMonitoramentoEnabled && (
                            <ListItemButton
                                className={classes.itemButton}
                                onClick={this.emitMcMonitoramento}
                                data-active={scope.activePage === '/mcMonitorados'}
                            >
                                <Tooltip title="Documentos Monitorados">
                                    <ListItemIcon>
                                        <VisibilityIcon className={classes.svg} />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText primary="Documentos Monitorados" />
                            </ListItemButton>
                        )}
                        {scope.mcPjEnabled && (
                            <ListItemButton
                                className={classes.itemButton}
                                onClick={this.emitMcPj}
                                data-active={scope.activePage === '/mcPj'}
                            >
                                <Tooltip title="Pessoa Jurídica">
                                    <ListItemIcon>
                                        <BusinessIcon className={classes.svg} />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText primary="Pessoa Jurídica" />
                            </ListItemButton>
                        )}
                        {scope.mcPfEnabled && (
                            <ListItemButton
                                className={classes.itemButton}
                                onClick={this.emitMcPf}
                                data-active={scope.activePage === '/mcPf'}
                            >
                                <Tooltip title="Pessoa Física">
                                    <ListItemIcon>
                                        <BadgeIcon className={classes.svg} />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText primary="Pessoa Física" />
                            </ListItemButton>
                        )}
                        {scope.mcSerasaEnabled && (
                            <ListItemButton
                                className={classes.itemButton}
                                onClick={this.emitMcSerasa}
                                data-active={scope.activePage === '/mcSerasa'}
                            >
                                <Tooltip title="Serasa">
                                    <ListItemIcon>
                                        <AccountBalanceIcon className={classes.svg} />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText primary="Serasa" />
                            </ListItemButton>
                        )}
                        {scope.mcSerasaHistoryEnabled && (
                            <ListItemButton
                                className={classes.itemButton}
                                onClick={this.emitMcSerasaHistory}
                                data-active={scope.activePage === '/mcSerasaHistory'}
                            >
                                <Tooltip title="Histórico Serasa">
                                    <ListItemIcon>
                                        <HistoryIcon className={classes.svg} />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText primary="Histórico Serasa" />
                            </ListItemButton>
                        )}
                        {scope.mcSerproEnabled && (
                            <ListItemButton
                                className={classes.itemButton}
                                onClick={this.emitMcSerpro}
                                data-active={scope.activePage === '/mcSerpro'}
                            >
                                <Tooltip title="NF-e / SERPRO">
                                    <ListItemIcon>
                                        <FactCheckIcon className={classes.svg} />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText primary="NF-e / SERPRO" />
                            </ListItemButton>
                        )}
                    </>
                )}
                {scope.userManagerEnabled && (
                    <>
                        <Divider sx={{ my: 1 }} className={classes.divider} />
                        <ListItemButton
                            className={classes.itemButton}
                            onClick={this.emitUserManager}
                            data-active={scope.activePage === '/userManager'}
                        >
                            <Tooltip title="Gerenciar Usuários">
                                <ListItemIcon>
                                    <ManageAccountsIcon className={classes.svg} />
                                </ListItemIcon>
                            </Tooltip>
                            <ListItemText primary="Gerenciar Usuários" />
                        </ListItemButton>

                        <ListItemButton
                            className={classes.itemButton}
                            onClick={this.emitGroupManager}
                            data-active={scope.activePage === '/groupManager'}
                        >
                            <Tooltip title="Gerenciar Grupos">
                                <ListItemIcon>
                                    <GroupsIcon className={classes.svg} />
                                </ListItemIcon>
                            </Tooltip>
                            <ListItemText primary="Gerenciar Grupos" />
                        </ListItemButton>

                        {scope.accessAuditEnabled && (
                            <ListItemButton
                                className={classes.itemButton}
                                onClick={this.emitAccessAudit}
                                data-active={scope.activePage === '/accessAudit'}
                            >
                                <Tooltip title="Auditoria de Acesso">
                                    <ListItemIcon>
                                        <QueryStatsIcon className={classes.svg} />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText primary="Auditoria de Acesso" />
                            </ListItemButton>
                        )}

                        {scope.billingEnabled && (
                            <ListItemButton
                                className={classes.itemButton}
                                onClick={this.emitBilling}
                                data-active={scope.activePage === '/billing'}
                            >
                                <Tooltip title="Faturamento">
                                    <ListItemIcon>
                                        <RequestQuoteIcon className={classes.svg} />
                                    </ListItemIcon>
                                </Tooltip>
                                <ListItemText primary="Faturamento" />
                            </ListItemButton>
                        )}

                        <ListItemButton
                            onClick={this.emitConfigManager}
                            className={classes.settingsItem}
                            data-active={scope.activePage === '/configurations'}
                        >
                            <Tooltip title="Configurações">
                                <ListItemIcon>
                                    <SettingsIcon className={classes.svg} />
                                </ListItemIcon>
                            </Tooltip>
                            <ListItemText primary="Configurações" />
                        </ListItemButton>
                    </>
                )}
            </List>
        )
    }
}

export default classToFComponent(DrawerContentClass)
